import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import './index.css'
import App from './App.tsx'
import { useUI } from './store/ui'
import { hayVersionMasNueva } from './lib/versionApp'

/** Salida de emergencia: novaberry.pages.dev/?limpiar=1
 *
 *  Deja el navegador como recién instalado —service worker, cachés y
 *  datos locales— antes de que la app llegue siquiera a arrancar. Sirve
 *  para el caso en que un service worker viejo quedó atascado sirviendo
 *  archivos de dos versiones distintas: ahí la app ni alcanza a
 *  dibujarse, así que ningún botón dentro de la app puede salvarla.
 *  Es un link que se puede mandar por WhatsApp y listo. */
if (new URLSearchParams(location.search).has('limpiar')) {
  document.body.innerHTML =
    '<div style="font:600 16px system-ui;display:grid;place-items:center;height:100dvh;color:#1b5e37">Limpiando…</div>'
  void (async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registros = await navigator.serviceWorker.getRegistrations()
        await Promise.all(registros.map((r) => r.unregister()))
      }
      if ('caches' in window) {
        const claves = await caches.keys()
        await Promise.all(claves.map((k) => caches.delete(k)))
      }
      localStorage.clear()
      sessionStorage.clear()
    } finally {
      // Con "recien" el navegador no puede reutilizar nada suyo, y sin
      // el "limpiar" no vuelve a entrar en este mismo camino.
      location.replace(`/?recien=${Date.now()}`)
    }
  })()
  throw new Error('limpiando')
}

// Cómo se actualiza la app, en dos partes que hay que leer juntas:
//
//  1. El service worker nuevo SIEMPRE toma el control apenas se instala
//     (ver sw.ts). Así una versión publicada llega de verdad: basta con
//     recargar para tenerla. Dejarlo "esperando" no funciona —solo se
//     activa al cerrar todas las ventanas, y en un celular la app queda
//     abierta para siempre, así que las versiones nunca llegaban.
//
//  2. La página NUNCA se recarga sola. Recargar encima de alguien que
//     está vendiendo le borra el trabajo a medio hacer —eso era lo que
//     hacía aparecer la pantalla de carga sin aviso—. En su lugar sale
//     un aviso con un botón, y la recarga la decide la persona.
const avisarVersionNueva = () => useUI.getState().setActualizacionLista(true)

if ('serviceWorker' in navigator) {
  // OJO con "controllerchange": se dispara en DOS situaciones que
  // parecen iguales desde acá pero no lo son.
  //
  //   a) Llegó una versión nueva y reemplazó a la anterior. Esto sí hay
  //      que avisarlo.
  //   b) No había ningún service worker todavía y recién ahora se
  //      registró el primero. Esto NO es una versión nueva: es el
  //      arranque normal la primera vez.
  //
  // Confundir (b) con (a) causaba un bucle sin salida: el botón
  // "Actualizar" borra el service worker y recarga, al volver se
  // registra uno nuevo desde cero, eso disparaba el evento, salía el
  // aviso otra vez... y así para siempre, sin que actualizar nada
  // sirviera.
  //
  // Se distinguen mirando si YA había un service worker al mando cuando
  // arrancó esta página: si no había, el primer cambio es el caso (b).
  const habiaServiceWorker = Boolean(navigator.serviceWorker.controller)
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (habiaServiceWorker) avisarVersionNueva()
  })

  registerSW({
    immediate: true,
    onNeedRefresh: avisarVersionNueva,
    onRegisteredSW(_url, registration) {
      if (!registration) return
      // Por si alguien deja la app abierta muchas horas seguidas: revisa
      // cada 30 minutos si salió una versión nueva.
      setInterval(() => registration.update(), 30 * 60 * 1000)
    },
  })
}

//  3. Y por si todo lo anterior falla —que ya pasó—, este es el control
//     que no depende del service worker: se le pregunta al servidor,
//     salteándose cualquier caché, si el código que está corriendo es el
//     último. Si no lo es, sale el aviso igual. Es la garantía de que
//     una versión publicada nunca se quede invisible para siempre.
const revisarVersion = () => {
  void hayVersionMasNueva().then((hayNueva) => {
    if (hayNueva) avisarVersionNueva()
  })
}
revisarVersion()
setInterval(revisarVersion, 5 * 60 * 1000)
// Volver a la app después de dejarla en segundo plano es el momento más
// cómodo para enterarse: no interrumpe nada a medio hacer.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') revisarVersion()
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
